import CircleIcon from '@mui/icons-material/Circle';
import { Timeline, TimelineItem } from '@mui/lab';
import { Box, Grid, Typography, FormControlLabel, styled, Button } from '@mui/material';
import React, { useState } from 'react';
import { RiPulseFill } from 'react-icons/ri';
import { IJobCandidateInteractionResponse } from 'wisestep-api';

import { activityFormattedDate } from '../../helper/DateOperations/DateOperations';
import WsColors from '../../WsColors';
import { UpdatedDetailsTimeline } from '../UpdatedDetailsTimeline';
import WsTimeLine, { WsActivityProps } from '../WsTimeLine/WsTimeLine';
import WsTypography, { WsTypoColors, WsTypoVariants } from '../WsTypography/WsTypography';

type ColorType = {
  bgColor: string;
};

const SwitchButton = styled(Button)(({ bgColor }: ColorType) => ({
  backgroundColor: bgColor,
  width: '100px',
  border: '0.2px solid',
  borderRadius: 0,
}));

export interface SubmissionTimelineProps {
  candidate: IJobCandidateInteractionResponse;
  displayAllDetails?: boolean;
  taskLisiting?: JSX.Element;
  getFieldSettings: (isMapping: boolean) => any;
  emailLogs?: React.ReactElement;
  hideTimeline?: boolean;
  hideTimelineHeader?: boolean;
  withoutOppositeContent?: boolean;
}

const SubmissionTimeline = ({
  candidate,
  displayAllDetails = false,
  taskLisiting,
  getFieldSettings,
  emailLogs,
  hideTimeline = false,
  hideTimelineHeader = false,
  withoutOppositeContent = false,
}: SubmissionTimelineProps) => {
  const [planIndex, setPlanIndex] = useState<number>(0);
  const activityLists: WsActivityProps[] = [];
  const stageAndStateActions = candidate.candidateNotes ?? [];
  const sortedActions = stageAndStateActions.sort((actionA, actionB) =>
    actionB.createdAt.localeCompare(actionA.createdAt)
  );
  sortedActions.forEach((actions, index) =>
    activityLists.push({
      activity: (
        <TimelineItem>
          <Grid container>
            <Grid item xs={12}>
              <WsTypography color={WsTypoColors.TERTIARY}>
                {`${activityFormattedDate(actions.createdAt)} by ${actions.createdBy}`}
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant={WsTypoVariants.SUB_TITLE}
                dangerouslySetInnerHTML={{ __html: actions.notes }}
              ></Typography>
            </Grid>
            {index === 0 && (
              <Grid item xs={12}>
                {taskLisiting}
              </Grid>
            )}
          </Grid>
        </TimelineItem>
      ),
      leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
    })
  );

  return (
    <>
      {emailLogs && (
        <Box textAlign="center" mt={2}>
          <SwitchButton
            variant="outlined"
            bgColor={planIndex === 0 ? WsColors.TheBlues100 : WsColors.White}
            onClick={() => setPlanIndex(0)}
          >
            Updates
          </SwitchButton>
          <SwitchButton
            variant="outlined"
            bgColor={planIndex === 1 ? WsColors.TheBlues100 : WsColors.White}
            onClick={() => setPlanIndex(1)}
          >
            SMS
          </SwitchButton>
          <SwitchButton
            variant="outlined"
            bgColor={planIndex === 2 ? WsColors.TheBlues100 : WsColors.White}
            onClick={() => setPlanIndex(2)}
          >
            Email
          </SwitchButton>
        </Box>
      )}
      {planIndex === 0 && (
        <>
          {candidate.modifiedOn && candidate.modifiedBy && !hideTimelineHeader && (
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Box pl={1} pt={0.2}>
                    <RiPulseFill color={WsColors.TheBlues500} />
                  </Box>
                }
                label={
                  <Box pl={1}>
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.BLACK}
                      cursor="pointer"
                      hoverText={candidate.modifiedBy}
                    >
                      {`Last updated ${activityFormattedDate(candidate.modifiedOn)}  by ${
                        candidate.modifiedBy
                      } `}
                    </WsTypography>
                  </Box>
                }
              />
            </Box>
          )}
          {!hideTimeline && (
            <Timeline>
              <Box {...(!withoutOppositeContent && { ml: '-100%' })}>
                <WsTimeLine
                  activityList={activityLists}
                  timeLineDotVariant={'outlined'}
                  {...(withoutOppositeContent && { withoutOppositeContent })}
                />
              </Box>
            </Timeline>
          )}
          {displayAllDetails && (
            <Box mt={1}>
              <UpdatedDetailsTimeline candidate={candidate} getFieldSettings={getFieldSettings} />
            </Box>
          )}
        </>
      )}
      {planIndex === 1 && (
        <Grid item xs={12} display="flex" justifyContent="center" pt={12}>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY}>
            Coming Soon
          </WsTypography>
        </Grid>
      )}
      {planIndex === 2 && emailLogs && <>{emailLogs}</>}
    </>
  );
};

export default SubmissionTimeline;
